import $j5SlF$babelruntimehelpersesmextends from "@babel/runtime/helpers/esm/extends";
import {forwardRef as $j5SlF$forwardRef, useRef as $j5SlF$useRef, useCallback as $j5SlF$useCallback, useState as $j5SlF$useState, useEffect as $j5SlF$useEffect, createElement as $j5SlF$createElement, Fragment as $j5SlF$Fragment} from "react";
import $j5SlF$reactdom from "react-dom";
import {useComposedRefs as $j5SlF$useComposedRefs} from "@radix-ui/react-compose-refs";
import {Primitive as $j5SlF$Primitive} from "@radix-ui/react-primitive";
import {useLayoutEffect as $j5SlF$useLayoutEffect} from "@radix-ui/react-use-layout-effect";







const $d378aa7390afdd5e$var$ROLES = {
    polite: 'status',
    assertive: 'alert',
    off: 'none'
};
const $d378aa7390afdd5e$var$listenerMap = new Map();
/* -------------------------------------------------------------------------------------------------
 * Announce
 * -----------------------------------------------------------------------------------------------*/ const $d378aa7390afdd5e$var$NAME = 'Announce';
const $d378aa7390afdd5e$export$722c724746ce2960 = /*#__PURE__*/ $j5SlF$forwardRef((props, forwardedRef)=>{
    const { 'aria-relevant': ariaRelevant , children: children , type: type = 'polite' , role: role = $d378aa7390afdd5e$var$ROLES[type] , regionIdentifier: regionIdentifier , ...regionProps } = props;
    const ariaAtomic = [
        'true',
        true
    ].includes(regionProps['aria-atomic']); // The region is appended to the root document node, which is usually the global `document` but in
    // some contexts may be another node. After the Announce element ref is attached, we set the
    // ownerDocumentRef to make sure we have the right root node. We should only need to do this once.
    const ownerDocumentRef = $j5SlF$useRef(document);
    const setOwnerDocumentFromRef = $j5SlF$useCallback((node)=>{
        if (node) ownerDocumentRef.current = node.ownerDocument;
    }, []);
    const ownRef = $j5SlF$useRef(null);
    const ref = $j5SlF$useComposedRefs(forwardedRef, ownRef, setOwnerDocumentFromRef);
    const [region, setRegion] = $j5SlF$useState();
    const relevant = ariaRelevant ? Array.isArray(ariaRelevant) ? ariaRelevant.join(' ') : ariaRelevant : undefined;
    const getLiveRegionElement = $j5SlF$useCallback(()=>{
        const ownerDocument = ownerDocumentRef.current;
        const regionConfig = {
            type: type,
            role: role,
            relevant: relevant,
            id: regionIdentifier,
            atomic: ariaAtomic
        };
        const regionSelector = $d378aa7390afdd5e$var$buildSelector(regionConfig);
        const element = ownerDocument.querySelector(regionSelector);
        return element || $d378aa7390afdd5e$var$buildLiveRegionElement(ownerDocument, regionConfig);
    }, [
        ariaAtomic,
        relevant,
        role,
        type,
        regionIdentifier
    ]);
    $j5SlF$useLayoutEffect(()=>{
        setRegion(getLiveRegionElement());
    }, [
        getLiveRegionElement
    ]); // In some screen reader/browser combinations, alerts coming from an inactive browser tab may be
    // announced, which is a confusing experience for a user interacting with a completely different
    // page. When the page visibility changes we'll update the `role` and `aria-live` attributes of
    // our region element to prevent that.
    // https://inclusive-components.design/notifications/#restrictingmessagestocontexts
    $j5SlF$useEffect(()=>{
        const ownerDocument = ownerDocumentRef.current;
        function updateAttributesOnVisibilityChange() {
            regionElement.setAttribute('role', ownerDocument.hidden ? 'none' : role);
            regionElement.setAttribute('aria-live', ownerDocument.hidden ? 'off' : type);
        } // Ok, so this might look a little weird and confusing, but here's what's going on:
        //   - We need to hide `aria-live` regions via a global event listener, as noted in the comment
        //     above.
        //   - We only need one listener per region. Keep in mind that each `Announce` does not
        //     necessarily generate a unique live region element.
        //   - We track whether or not a listener has already been attached for a given region in a map
        //     so we can skip these effects after `Announce` is used again with a shared live region.
        const regionElement = getLiveRegionElement();
        if (!$d378aa7390afdd5e$var$listenerMap.get(regionElement)) {
            ownerDocument.addEventListener('visibilitychange', updateAttributesOnVisibilityChange);
            $d378aa7390afdd5e$var$listenerMap.set(regionElement, 1);
        } else {
            const announceCount = $d378aa7390afdd5e$var$listenerMap.get(regionElement);
            $d378aa7390afdd5e$var$listenerMap.set(regionElement, announceCount + 1);
        }
        return function() {
            const announceCount = $d378aa7390afdd5e$var$listenerMap.get(regionElement);
            $d378aa7390afdd5e$var$listenerMap.set(regionElement, announceCount - 1);
            if (announceCount === 1) ownerDocument.removeEventListener('visibilitychange', updateAttributesOnVisibilityChange);
        };
    }, [
        getLiveRegionElement,
        role,
        type
    ]);
    return /*#__PURE__*/ $j5SlF$createElement($j5SlF$Fragment, null, /*#__PURE__*/ $j5SlF$createElement($j5SlF$Primitive.div, $j5SlF$babelruntimehelpersesmextends({}, regionProps, {
        ref: ref
    }), children), region && /*#__PURE__*/ $j5SlF$reactdom.createPortal(/*#__PURE__*/ $j5SlF$createElement("div", null, children), region));
});
/*#__PURE__*/ Object.assign($d378aa7390afdd5e$export$722c724746ce2960, {
    displayName: $d378aa7390afdd5e$var$NAME
});
/* ---------------------------------------------------------------------------------------------- */ function $d378aa7390afdd5e$var$buildLiveRegionElement(ownerDocument, { type: type , relevant: relevant , role: role , atomic: atomic , id: id  }) {
    const element = ownerDocument.createElement('div');
    element.setAttribute($d378aa7390afdd5e$var$getLiveRegionPartDataAttr(id), '');
    element.setAttribute('style', 'position: absolute; top: -1px; width: 1px; height: 1px; overflow: hidden;');
    ownerDocument.body.appendChild(element);
    element.setAttribute('aria-live', type);
    element.setAttribute('aria-atomic', String(atomic || false));
    element.setAttribute('role', role);
    if (relevant) element.setAttribute('aria-relevant', relevant);
    return element;
}
function $d378aa7390afdd5e$var$buildSelector({ type: type , relevant: relevant , role: role , atomic: atomic , id: id  }) {
    return `[${$d378aa7390afdd5e$var$getLiveRegionPartDataAttr(id)}]${[
        [
            'aria-live',
            type
        ],
        [
            'aria-atomic',
            atomic
        ],
        [
            'aria-relevant',
            relevant
        ],
        [
            'role',
            role
        ]
    ].filter(([, val])=>!!val
    ).map(([attr, val])=>`[${attr}=${val}]`
    ).join('')}`;
}
function $d378aa7390afdd5e$var$getLiveRegionPartDataAttr(id) {
    return 'data-radix-announce-region' + (id ? `-${id}` : '');
}
const $d378aa7390afdd5e$export$be92b6f5f03c0fe9 = $d378aa7390afdd5e$export$722c724746ce2960;




export {$d378aa7390afdd5e$export$722c724746ce2960 as Announce, $d378aa7390afdd5e$export$be92b6f5f03c0fe9 as Root};
//# sourceMappingURL=index.mjs.map
